import { Grid, makeStyles, Box, Typography } from '@material-ui/core'
import React from 'react'
import CountUp from 'react-countup'
import { useSelector } from 'react-redux'
import Icon from '@mdi/react'
import {
  mdiCheckCircleOutline,
  mdiCalendarRangeOutline,
  mdiCashClock,
} from '@mdi/js'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import colors from '../../../../assets/colors'
import SubscriptionAlertBanner from '../SubscriptionAlertBanner'

import PaymentMethod from './PaymentMethod'
import StatusChip, { getPlanStatusTone } from './StatusChip'
import Addons from './Addons'
import UsersUsage from './UsersUsage'
import BillingProfile from './BillingProfile'
import { nextInvoiceReductionCents } from './addonHelpers'

const CANCELLATION_SCHEDULED_STATUS = 35

const useStyles = makeStyles({
  card: {
    border: '1px solid',
    borderColor: colors.graylight,
    padding: '20px 16px',
    borderRadius: '12px',
  },
  cardTitle: {
    fontSize: '20px',
    color: colors.primary,
    fontWeight: 'bold',
  },
})

const Card = ({ title = '', children }) => {
  const classes = useStyles()

  return (
    <Box className={classes.card}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className={classes.cardTitle}>{title}</Typography>
        </Grid>

        {children}
      </Grid>
    </Box>
  )
}

const formatDate = iso => {
  if (!iso) return null
  try {
    return format(new Date(iso), 'dd/MM/yyyy', { locale: ptBR })
  } catch (e) {
    return null
  }
}

// Estado "vivo" da assinatura no gateway (Pagar.me): período do ciclo vigente
// e data da próxima cobrança. Alimentado por fetchSubscriptionGateway.
const CurrentCycleInfo = () => {
  const gatewayInfo = useSelector(state => state.subscription.gatewayInfo)

  if (!gatewayInfo) return null

  const cycle = gatewayInfo.current_cycle
  const cycleStart = formatDate(cycle?.start_at)
  const cycleEnd = formatDate(cycle?.end_at)
  const nextBilling = formatDate(cycle?.billing_at)

  if (!cycleStart && !cycleEnd && !nextBilling) return null

  return (
    <Box
      style={{
        border: '1px solid',
        borderColor: colors.graylight,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {cycleStart && cycleEnd && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icon
            path={mdiCalendarRangeOutline}
            size={0.9}
            color={colors.primary}
          />
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography style={{ fontSize: '12px', color: colors.textGray }}>
              Ciclo atual
            </Typography>
            <Typography
              style={{
                fontSize: '14px',
                color: colors.primary,
                fontWeight: 'bold',
              }}
            >
              {cycleStart} até {cycleEnd}
            </Typography>
          </Box>
        </Box>
      )}

      {nextBilling && (
        <Box style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icon path={mdiCashClock} size={0.9} color={colors.info} />
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography style={{ fontSize: '12px', color: colors.textGray }}>
              Próxima cobrança
            </Typography>
            <Typography
              style={{
                fontSize: '14px',
                color: colors.primary,
                fontWeight: 'bold',
              }}
            >
              {nextBilling}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

// Aviso da janela de cancelamento agendado (status 35): a assinatura já foi
// cancelada no gateway, mas o acesso continua liberado até effective_cancel_at.
// Reativar é feito pelo botão 'Assinar' de PaymentMethod, que reenvia o método
// de pagamento já selecionado (mesmo fluxo de assinar) — sem ação aqui.
const ScheduledCancellationBanner = () => {
  const planDetails = useSelector(state => state.subscription.planDetails)

  if (planDetails?.status?.var_id != CANCELLATION_SCHEDULED_STATUS) return null

  const effectiveDate = formatDate(planDetails?.effective_cancel_at)

  return (
    <SubscriptionAlertBanner
      tone="info"
      message={
        effectiveDate
          ? `Sua assinatura foi cancelada e o acesso continua até ${effectiveDate}. Para continuar, clique em "Assinar" nos dados de pagamento.`
          : 'Sua assinatura foi cancelada. Você ainda tem acesso liberado até o fim do ciclo atual.'
      }
    />
  )
}

const PlanDetails = () => {
  const planDetails = useSelector(state => state.subscription.planDetails)
  const contractedAddons = useSelector(
    state => state.subscription.contractedAddons
  )
  const classes = useStyles()

  // o total do plano segue a quantidade de ACESSO dos add-ons — ou seja, o que
  // este ciclo já pagou — e ainda inclui os add-ons com cancelamento agendado.
  // Havendo redução ou cancelamento agendado, a próxima fatura vem menor, e é
  // ela que o cliente precisa ver (billed_quantity).
  const reductionCents = nextInvoiceReductionCents(contractedAddons)
  const nextInvoice =
    planDetails?.total != null
      ? Math.max(0, Number(planDetails.total) - reductionCents / 100)
      : null

  const contUpMoney = money => {
    return (
      <CountUp
        end={money}
        duration={0.7}
        decimal=","
        decimals={2}
        prefix="R$"
        separator="."
      />
    )
  }

  return (
    <Grid container xs={12} spacing={2}>
      <Grid item xs={12}>
        <ScheduledCancellationBanner />
      </Grid>

      <Grid item md={6} sm={6} xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={classes.card}>
              {planDetails && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            style={{
                              fontWeight: 'bold',
                              fontSize: '20px',
                              color: colors.primary,
                            }}
                          >
                            {planDetails?.name}
                          </Typography>
                          <StatusChip
                            label={planDetails?.status?.value}
                            tone={getPlanStatusTone(
                              planDetails?.status?.var_id
                            )}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '4px',
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: '28px',
                              color: colors.primary,
                              fontWeight: 'bold',
                            }}
                          >
                            {contUpMoney(
                              planDetails?.total ?? planDetails?.price ?? 0
                            )}
                          </Typography>
                          <Typography
                            style={{ fontSize: '12px', color: colors.textGray }}
                          >
                            / mês
                          </Typography>
                        </Box>
                        {planDetails?.total != null &&
                          planDetails?.total !== planDetails?.price && (
                            <Typography
                              style={{
                                fontSize: '12px',
                                color: colors.textGray,
                              }}
                            >
                              Plano base: {contUpMoney(planDetails?.price || 0)}
                            </Typography>
                          )}
                        {reductionCents > 0 && nextInvoice != null && (
                          <Typography
                            style={{
                              fontSize: '12px',
                              color: colors.textGray,
                            }}
                          >
                            Próxima fatura: {contUpMoney(nextInvoice)} — reflete
                            as reduções e cancelamentos agendados nos serviços
                            adicionais; o valor acima é o deste ciclo, já pago.
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Grid
                          container
                          justifyContent="space-between"
                          wrap="wrap"
                        >
                          {planDetails?.advantages?.map((advantage, index) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              key={`plan-advantage-${index}`}
                            >
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <Box
                                  style={{
                                    display: 'flex',
                                    gap: '12px',
                                  }}
                                >
                                  <Icon
                                    path={mdiCheckCircleOutline}
                                    size={0.8}
                                    color={colors.sucess}
                                  />
                                  <Typography
                                    style={{ color: colors.textGray }}
                                  >
                                    {advantage?.label}: {advantage?.value}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      style={{
                        borderBottom: '1px solid',
                        borderColor: colors.graylight,
                        width: '100%',
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Addons />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <UsersUsage />
          </Grid>
        </Grid>
      </Grid>

      <Grid item md={6} sm={6} xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card title="Dados de cobrança">
              <BillingProfile />
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card title="Dados de pagamento">
              <Grid item xs={12}>
                <CurrentCycleInfo />
              </Grid>

              <Grid item xs={12}>
                <PaymentMethod />
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default PlanDetails
